import { z } from 'zod';

/**
 * Domain enums shared by front-end and back-end.
 *
 * Values are stable identifiers stored in the database — never display them
 * directly. The UI resolves each value through the i18n dictionary so it can be
 * shown in Lao or English (see docs/spec.md §8.1).
 */

/** docs/spec.md §6 — 4 roles. Pending final confirmation before P2. */
export const userRoles = ['admin', 'doctor', 'reception', 'lab'] as const;
export const userRoleSchema = z.enum(userRoles);
export type UserRole = z.infer<typeof userRoleSchema>;

export const appointmentStatuses = [
  'pending',
  'confirmed',
  'arrived',
  'done',
  'cancelled',
  'no_show',
] as const;
export const appointmentStatusSchema = z.enum(appointmentStatuses);
export type AppointmentStatus = z.infer<typeof appointmentStatusSchema>;

export const invoiceStatuses = ['draft', 'unpaid', 'partial', 'paid', 'void'] as const;
export const invoiceStatusSchema = z.enum(invoiceStatuses);
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>;

export const invoiceItemSources = ['service', 'drug', 'lab', 'custom'] as const;
export const invoiceItemSourceSchema = z.enum(invoiceItemSources);
export type InvoiceItemSource = z.infer<typeof invoiceItemSourceSchema>;

export const paymentMethods = ['cash', 'transfer', 'card'] as const;
export const paymentMethodSchema = z.enum(paymentMethods);
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

export const stockMovementTypes = ['in', 'out', 'adjust', 'sale', 'return'] as const;
export const stockMovementTypeSchema = z.enum(stockMovementTypes);
export type StockMovementType = z.infer<typeof stockMovementTypeSchema>;

export const saleStatuses = ['completed', 'void'] as const;
export const saleStatusSchema = z.enum(saleStatuses);
export type SaleStatus = z.infer<typeof saleStatusSchema>;

export const labOrderStatuses = ['ordered', 'in_progress', 'completed', 'cancelled'] as const;
export const labOrderStatusSchema = z.enum(labOrderStatuses);
export type LabOrderStatus = z.infer<typeof labOrderStatusSchema>;

export const labPanels = ['chemistry', 'cbc', 'hematology', 'immunology', 'urine'] as const;
export const labPanelSchema = z.enum(labPanels);
export type LabPanel = z.infer<typeof labPanelSchema>;

/**
 * Printed lab reports use the single letters L / H, with blank meaning normal —
 * confirmed against the clinic's paper form (docs/spec.md §4.5).
 */
export const labFlags = ['', 'L', 'H'] as const;
export const labFlagSchema = z.enum(labFlags);
export type LabFlag = z.infer<typeof labFlagSchema>;

export const genders = ['female', 'male', 'other'] as const;
export const genderSchema = z.enum(genders);
export type Gender = z.infer<typeof genderSchema>;

/**
 * ABO blood groups. Free text previously allowed "o", "O+", "โอ" and blanks to
 * mean the same thing, which makes the field useless for anything but reading
 * back what someone typed.
 *
 * Rh factor is deliberately not folded in here — the clinic records it as its
 * own lab test ("Rh(D) Typing" in `lab_test_catalog`), so combining them would
 * duplicate a result that already has a home.
 */
export const bloodTypes = ['A', 'B', 'AB', 'O'] as const;
export const bloodTypeSchema = z.enum(bloodTypes);
export type BloodType = z.infer<typeof bloodTypeSchema>;

/** Phase 1 is LAK-only; the other two exist so the schema needs no migration later. */
export const currencies = ['LAK', 'THB', 'USD'] as const;
export const currencySchema = z.enum(currencies);
export type Currency = z.infer<typeof currencySchema>;

/* ---- UI preferences (docs/spec.md §8.2) ---- */

export const langs = ['la', 'en'] as const;
export const langSchema = z.enum(langs);
export type Lang = z.infer<typeof langSchema>;

export const accentThemes = ['rose', 'red', 'berry'] as const;
export const accentThemeSchema = z.enum(accentThemes);
export type AccentTheme = z.infer<typeof accentThemeSchema>;

export const colorModes = ['light', 'dark'] as const;
export const colorModeSchema = z.enum(colorModes);
export type ColorMode = z.infer<typeof colorModeSchema>;
